<template>
  <CloseHeader :title="`${t('edit_my_info')}`" />

  <div class="app-container">
    <div class="info-area basic">
      <strong>{{ t('label_basic_info') }}</strong>
      <ul class="inr-list">
        <li>
          <p class="dt">{{ t('sconchat') }} ID</p>
          <p class="dd">{{ form.loginId }}</p>
        </li>
        <li class="change-item">
          <div>
            <p class="dt">{{ t('label_name') }}</p>
            <p class="dd">{{ form.nickname }}</p>
          </div>
          <button type="button" class="line-btn round-btn" @click="openChangeInfoPopup('nickname')">
            <span>{{ t('label_change_short') }}</span>
          </button>
        </li>
        <li class="change-item">
          <div>
            <p class="dt">{{ t('label_email') }}</p>
            <p class="dd">{{ form.email ?? '-' }}</p>
          </div>
          <button type="button" class="line-btn round-btn" @click="openChangeInfoPopup('email')">
            <span>{{ t('label_change_short') }}</span>
          </button>
        </li>
      </ul>
    </div>

    <AppDivider />

    <div class="info-area add">
      <strong>{{ t('label_additional_info') }}</strong>
      <ul class="inr-list">
        <li class="change-item">
          <div>
            <p class="dt">{{ t('usage_language') }}</p>
            <p class="dd">{{ form.languageCode.name }}</p>
          </div>
          <button type="button" class="line-btn round-btn" @click="openLanguagePopup">
            <span>{{ t('label_change_short') }}</span>
          </button>
        </li>
        <li v-if="userInfo?.userType === 'HOST'" class="change-item">
          <div>
            <p class="dt">{{ t('label_country') }}</p>
            <p class="dd">{{ form.countryCode ? form.countryCode.name : '-' }}</p>
          </div>
          <button type="button" class="line-btn round-btn" @click="openCountryPopup">
            <span>{{ t('label_change_short') }}</span>
          </button>
        </li>
      </ul>

      <div class="save-guide">
        <div class="app-ck-wrap ck-item">
          <input id="agree04" v-model="form.storeAgreement" type="checkbox" class="app-ck" />
          <label for="agree04" class="app-ck-icon">
            <IconCheck />
          </label>
          <span>{{ t('save_transcription_effect') }}</span>
        </div>

        <div class="gray-box">
          {{ t('interpretation_record_description_1') }}<br />
          {{ t('interpretation_record_description_2') }}<br />
          <i18n-t keypath="interpretation_record_description_3">
            <template #policy>
              <a class="link-small" @click="goToPolicy">
                {{ t('privacy_policy') }}
              </a>
            </template>
          </i18n-t>
        </div>
      </div>
    </div>

    <AppDivider />

    <div class="info-area etc">
      <strong>{{ t('inquiry_title_etc') }}</strong>
      <div class="etc-wrap">
        <button type="button" class="m-btn" @click="onClickLogout">{{ t('button_logout') }}</button>
        <button v-if="isUserHost" type="button" class="m-btn non-btn" @click="openConfirmWithdrawPopup">
          {{ t('delete_account_short') }}
        </button>
        <!-- TODO: 인앱 결제시, 구현 예정
        <button type="button" class="m-btn non-btn">구독 해지하기</button> 
        -->
      </div>
    </div>

    <div class="app-btn-wrap">
      <button type="button" class="gray-btn l-btn" @click="close">
        <span>{{ t('button_cancel') }}</span>
      </button>
      <button type="button" class="primary-btn l-btn" @click="onSave">
        <span>{{ t('button_save2') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { updateEmployee } from '@/api/apiService';
import AppDivider from '@/components/domains/mobile/common/AppDivider.vue';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import BottomSheet from '@/components/domains/mobile/common/PopupBottomSheet.vue';
import PopupChangeUserInfo from '@/components/domains/mobile/popup/PopupChangeUserInfo.vue';
import PopupConfirmWithdraw from '@/components/domains/mobile/popup/PopupConfirmWithdraw.vue';
import PopupPolicyPrivacy from '@/components/domains/mobile/popup/PopupPolicyPrivacy.vue';
import PopupSelectCountry from '@/components/domains/mobile/popup/PopupSelectCountry.vue';
import PopupSelectLanguage from '@/components/domains/mobile/popup/PopupSelectLanguage.vue';
import IconCheck from '@/components/jh-app/icons/IconCheck.vue';
import { useLogin } from '@/composables/shared/useLogin';
import { setIsBack } from '@/router';
import { getLanguageByCode } from '@/static/languages';
import { useCountryStore } from '@/stores/country';
import { usePopupStore } from '@/stores/popup';
import { useUserStore } from '@/stores/user';
import { Country, Language } from '@/types/components';
import { secureStorageRemove } from '@/utils/native/nativeBridge';

const { t } = useI18n();
const router = useRouter();

const countryStore = useCountryStore();
const popupStore = usePopupStore();
const userStore = useUserStore();

const { goToLoginPage } = useLogin();

const { userInfo } = storeToRefs(userStore);

const form = ref();

const isUserHost = computed(() => {
  return userInfo.value?.userType === 'HOST';
});

const openChangeInfoPopup = (type: string) => {
  const popupId = popupStore.open(BottomSheet, {
    title: type === 'nickname' ? t('change_name') : t('change_email'),
    contentSlot: h(PopupChangeUserInfo),
    payload: form.value[type],
    showHandleBar: true,
    confirmButton: {
      action: (newVal: string) => {
        form.value[type] = newVal;
        popupStore.close(popupId);
      },
    },
  });
};

const openLanguagePopup = () => {
  popupStore.open(BottomSheet, {
    title: t('change_language'),
    contentSlot: h(PopupSelectLanguage),
    payload: form.value.languageCode,
    showCloseButton: true,
    confirmButton: {
      action: (language: Language) => {
        form.value.languageCode = language;
      },
    },
  });
};

const openCountryPopup = () => {
  popupStore.open(BottomSheet, {
    title: t('change_country'),
    contentSlot: h(PopupSelectCountry),
    payload: form.value.countryCode,
    showCloseButton: true,
    confirmButton: {
      action: (country: Country) => {
        form.value.countryCode = country;
      },
    },
  });
};

const openConfirmWithdrawPopup = () => {
  const popupId = popupStore.open(BottomSheet, {
    contentSlot: h(PopupConfirmWithdraw),
    showHandleBar: true,
    confirmButton: {
      action: () => {
        popupStore.close(popupId);
        router.push({ name: 'app-user-withdraw' });
      },
    },
  });
};

const goToPolicy = () => {
  popupStore.open(BottomSheet, {
    contentSlot: h(PopupPolicyPrivacy),
    showCloseButton: true,
  });
};

const onClickLogout = async () => {
  try {
    await secureStorageRemove({ key: 'authToken' });
    await secureStorageRemove({ key: 'authSession' });
  } finally {
    goToLoginPage();
  }
};

const close = () => {
  setIsBack(true);
  router.back();
};

const onSave = async () => {
  try {
    const { success } = await updateEmployee(userInfo.value.userId, null, {
      body: JSON.stringify({
        nickname: form.value.nickname,
        email: form.value.email,
        languageCode: form.value.languageCode?.code ?? null,
        countryCode: form.value.countryCode?.code ?? null,
        storeAgreement: form.value.storeAgreement,
        groupIds: [],
        sampleRate: '16000',
      }),
      voice: '',
    });

    if (!success) {
      popupStore.openToast(t('failed_to_save_info'));
      return;
    }

    await userStore.fetchUserInfo();
    popupStore.openToast(t('alert_save_completed'));
    close();
  } catch (error) {
    popupStore.openToast(t('failed_to_save_info'));
    console.error('사용자 정보 수정 실패: ', error);
  }
};

onMounted(async () => {
  await countryStore.requestCountries();
});

watch(
  () => userInfo.value,
  (newValue) => {
    if (!newValue) {
      return;
    }

    form.value = {
      ...newValue,
      languageCode: getLanguageByCode(newValue.languageCode),
      countryCode: newValue.countryCode && countryStore.getCountryByCode(newValue.countryCode),
    };
  },
  { immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
.app-container {
  padding-bottom: 4.5rem;
}
.info-area {
  &.basic {
    padding-top: 1rem;
  }
  strong {
    display: block;
    font-weight: 600;
    font-size: 1rem;
    padding-bottom: 0.5rem;
  }
  .inr-list {
    li {
      border-bottom: 1px solid $color-gray-40;
      padding: 1rem 0;
      p {
        font-size: $font-size-psmall;
        &.dt {
          color: $color-gray-200;
        }
      }
      &.change-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }
  &.add {
    .save-guide {
      padding: 1.5rem 0 0;
      .app-ck-wrap {
        span {
          font-size: $font-size-psmall;
        }
      }
      .gray-box {
        margin-top: 0.75rem;
        background: $gray10;
        border-radius: $size-s;
        padding: 1rem;
        font-size: $font-size-xs;
        line-height: 130%;
        a {
          font-size: $font-size-xs;
        }
      }
    }
  }
  &.etc {
    .etc-wrap {
      padding: 1rem 0;
      button {
        display: block;
        text-align: left;
        font-size: $font-size-psmall;
        &.non-btn {
          color: $color-gray-100;
        }
      }
    }
  }
}
</style>
